import React, { useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  Alert,
  AlertTitle,
  CircularProgress,
  Divider
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import CollectionTypeSelect from './CollectionTypeSelect';
import { convertToCSV, parseCSV, formatDataForCSV, getTemplateRow } from '../../utils/csvUtils';
import axiosInstance from '../../axiosInstance';

/**
 * Component for CSV import and export functionality
 */
const CSVImportExport = ({ onSnackbarMessage }) => {
  const [selectedCollectionType, setSelectedCollectionType] = useState('games');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [importData, setImportData] = useState(null);
  const [exportData, setExportData] = useState(null);
  const [importProgress, setImportProgress] = useState(null); // { done, total }

  const handleCollectionTypeChange = (event) => {
    setSelectedCollectionType(event.target.value);
    // Reset import data when collection type changes
    setImportData(null);
    setImportFile(null);
  };

  const handleExportCSV = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Get data for the selected collection type
      const response = await axiosInstance.get(`/import-export/export-csv/${selectedCollectionType}`);
      setExportData(response.data);
      
      // Convert data to CSV
      const csvData = convertToCSV(formatDataForCSV(response.data, selectedCollectionType));
      
      // Create a CSV file for download
      const dataBlob = new Blob([csvData], { type: 'text/csv' });
      const url = URL.createObjectURL(dataBlob);
      
      // Create a link and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = `intercollect-${selectedCollectionType}-export.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      onSnackbarMessage({
        open: true,
        message: 'CSV export successful! Your data has been downloaded.',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error exporting CSV data:', err);
      setError('Failed to export CSV data. Please try again.');
      onSnackbarMessage({
        open: true,
        message: 'CSV export failed. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCSVTemplate = () => {
    try {
      // Create template with headers
      const template = [getTemplateRow(selectedCollectionType)];
      const csvData = convertToCSV(template);
      
      // Create a CSV file for download
      const dataBlob = new Blob([csvData], { type: 'text/csv' });
      const url = URL.createObjectURL(dataBlob);
      
      // Create a link and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = `intercollect-${selectedCollectionType}-template.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      onSnackbarMessage({
        open: true,
        message: 'CSV template downloaded! Fill it with your data and import.',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error downloading CSV template:', err);
      setError('Failed to download CSV template. Please try again.');
      onSnackbarMessage({
        open: true,
        message: 'CSV template download failed. Please try again.',
        severity: 'error'
      });
    }
  };

  const handleImportFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setImportFile(file);
      
      // Read the file
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const csvString = e.target.result;
          const data = parseCSV(csvString);
          setImportData(data);
          
          // Show success message
          onSnackbarMessage({
            open: true,
            message: 'CSV file loaded successfully. Review the data and click Import to proceed.',
            severity: 'info'
          });
        } catch (err) {
          console.error('Error parsing CSV file:', err);
          setError('Invalid CSV file. Please upload a valid CSV file.');
          setImportData(null);
          onSnackbarMessage({
            open: true,
            message: 'Invalid CSV file. Please upload a valid CSV file.',
            severity: 'error'
          });
        }
      };
      reader.readAsText(file);
    }
  };

  const handleImportCSV = async () => {
    if (!importData) {
      setError('No import data available. Please upload a file first.');
      return;
    }

    const BATCH_SIZE = 15;
    const batches = [];
    for (let i = 0; i < importData.length; i += BATCH_SIZE) {
      batches.push(importData.slice(i, i + BATCH_SIZE));
    }

    setLoading(true);
    setError(null);
    setImportProgress({ done: 0, total: importData.length });

    const totals = { imported: 0, updated: 0, errors: [] };

    try {
      for (let i = 0; i < batches.length; i++) {
        const response = await axiosInstance.post(`/import-export/import-csv/${selectedCollectionType}`, {
          data: batches[i],
          options: { fillMissingInfo: false }
        });
        totals.imported += response.data.imported || 0;
        totals.updated += response.data.updated || 0;
        if (response.data.errors) totals.errors.push(...response.data.errors);
        setImportProgress({ done: Math.min((i + 1) * BATCH_SIZE, importData.length), total: importData.length });
      }

      if (totals.errors.length > 0) {
        onSnackbarMessage({
          open: true,
          message: `Import partially completed. ${totals.imported} imported, ${totals.updated} updated, ${totals.errors.length} errors.`,
          severity: 'warning'
        });
        setError(`Some items could not be imported:\n${totals.errors.map(e => `"${e.item?.name || 'Unknown'}" - ${e.error}`).join('\n')}`);
      } else {
        onSnackbarMessage({
          open: true,
          message: `Import completed! ${totals.imported} imported, ${totals.updated} updated.`,
          severity: 'success'
        });
        setImportData(null);
        setImportFile(null);
      }
    } catch (err) {
      console.error('Error importing CSV data:', err);
      const errorMessage = err.response?.data?.error || err.response?.data?.details || err.message || 'Unknown error';
      setError(`Failed to import CSV data: ${errorMessage}`);
      onSnackbarMessage({
        open: true,
        message: `CSV import failed: ${errorMessage}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
      setImportProgress(null);
    }
  };

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Select Collection Type
        </Typography>
        <CollectionTypeSelect
          value={selectedCollectionType}
          onChange={handleCollectionTypeChange}
          helperText="Select the collection or wishlist to import/export"
        />
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Export as CSV
        </Typography>
        <Typography variant="body2" paragraph>
          Export your {selectedCollectionType.replace('-', ' ')} to a CSV file that you can open in Excel or other spreadsheet applications.
        </Typography>
        
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={handleExportCSV}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Export to CSV'}
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Import from CSV
        </Typography>
        <Typography variant="body2" paragraph>
          Import items into your {selectedCollectionType.replace('-', ' ')} from a CSV file.
        </Typography>
        
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle>CSV Format</AlertTitle>
          <Typography variant="body2">
            Your CSV file should have headers matching the template. You can download a template below.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            If information is missing, the system will attempt to look up additional data for the items.
          </Typography>
        </Alert>
        
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadCSVTemplate}
          >
            Download Template
          </Button>
          <Button
            variant="contained"
            color="primary"
            component="label"
            startIcon={<UploadFileIcon />}
            disabled={loading}
          >
            Select CSV File
            <input
              type="file"
              accept=".csv"
              hidden
              onChange={handleImportFileChange}
            />
          </Button>
        </Box>
        
        {importFile && (
          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography>Selected file: {importFile.name}</Typography>
            <Typography>Size: {Math.round(importFile.size / 1024)} KB</Typography>
          </Alert>
        )}
        
        {importData && (
          <>
            <Alert severity="success" sx={{ mt: 3 }}>
              <AlertTitle>File Loaded Successfully</AlertTitle>
              <Typography>
                Items: {importData.length} rows found in CSV
              </Typography>
            </Alert>
            
            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleImportCSV}
                disabled={loading}
              >
                {loading
                  ? importProgress
                    ? `Importing… ${importProgress.done} / ${importProgress.total}`
                    : <CircularProgress size={24} />
                  : 'Import Data'}
              </Button>
            </Box>
          </>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mt: 3 }}>
            <AlertTitle>Error</AlertTitle>
            <Typography component="div" style={{ whiteSpace: 'pre-line' }}>
              {error}
            </Typography>
          </Alert>
        )}
      </Paper>
    </Box>
  );
};

export default CSVImportExport;
